import type {
  RefundRequest,
  RefundRequestDetail,
  RefundRequestProgressStage,
  RefundRequestStatus,
} from '@/@types/RefundRequest'

const BADGE_STATUS: Record<RefundRequestStatus, string> = {
  pending: 'pending',
  in_mediation: 'awaiting_payment',
  refunding: 'pending_refund',
  refunded: 'refunded',
  not_done: 'rejected',
  canceled: 'canceled',
  buyer_withdrew: 'canceled',
}

export function refundRequestBadgeStatus(status: string): string {
  return BADGE_STATUS[status as RefundRequestStatus] ?? 'canceled'
}

export function refundRequestAction(
  request: Pick<RefundRequest, 'can_respond' | 'status'>,
): 'respond' | 'conversation' | 'details' {
  if (request.status === 'in_mediation') return 'conversation'
  return request.can_respond ? 'respond' : 'details'
}

export function canRespondToRefundRequest(
  request: Pick<RefundRequest, 'can_respond' | 'status'> | null | undefined,
  hasRespondPermission: boolean,
): boolean {
  return Boolean(
    request &&
      hasRespondPermission &&
      request.can_respond &&
      (request.status === 'pending' || request.status === 'in_mediation'),
  )
}

export function canStartRefundMediation(
  request: Pick<RefundRequest, 'can_respond' | 'status'> | null | undefined,
  hasRespondPermission: boolean,
): boolean {
  return canRespondToRefundRequest(request, hasRespondPermission) && request?.status === 'pending'
}

export type MediationStepState = 'done' | 'current' | 'waiting'

export function mediationSteps(
  stage: RefundRequest['mediation_stage'],
): { seller: MediationStepState; buyer: MediationStepState } {
  if (stage === 'seller_turn') return { seller: 'current', buyer: 'waiting' }
  if (stage === 'buyer_turn') return { seller: 'done', buyer: 'current' }
  return { seller: 'done', buyer: 'done' }
}

export const ATTACHMENT_MIMES = ['application/pdf', 'image/png', 'image/jpeg']
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024

export function attachmentError(file: Pick<File, 'type' | 'size'>): 'type' | 'size' | null {
  if (!ATTACHMENT_MIMES.includes(file.type)) return 'type'
  if (file.size > ATTACHMENT_MAX_BYTES) return 'size'
  return null
}

export type WarrantyState = 'ending' | 'open' | 'expired' | 'closed'

export function refundRequestWarrantyState(
  request: Pick<RefundRequest, 'status' | 'warranty_days_left' | 'deadline_ending'>,
): WarrantyState {
  const open = request.status === 'pending' || request.status === 'in_mediation'
  if (!open || request.warranty_days_left === null) return 'closed'
  if (request.deadline_ending) return 'ending'
  if (request.warranty_days_left <= 0) return 'expired'
  return 'open'
}

export type TimeLeft = { unit: 'hours' | 'minutes'; value: number } | { unit: 'overdue' }

export function responseTimeLeft(dueAt: string | null | undefined, now: Date = new Date()): TimeLeft | null {
  if (!dueAt) return null

  const minutes = Math.floor((new Date(dueAt).getTime() - now.getTime()) / 60000)

  if (minutes <= 0) return { unit: 'overdue' }
  if (minutes < 60) return { unit: 'minutes', value: minutes }
  return { unit: 'hours', value: Math.floor(minutes / 60) }
}

export function responseWindowHours(
  request: Pick<RefundRequest, 'requested_at' | 'response_due_at'>,
): number | null {
  if (!request.requested_at || !request.response_due_at) return null

  return Math.round((new Date(request.response_due_at).getTime() - new Date(request.requested_at).getTime()) / 3600000)
}

export type ProgressStepState = 'done' | 'current' | 'warn'

export interface ProgressStep {
  key: string
  state: ProgressStepState
  at: string | null
}

const APPROVED_STEP: Record<string, string> = {
  seller: 'approved_seller',
  auto: 'approved_auto',
  buyer_kept: 'approved_buyer_kept',
  admin: 'approved_admin',
  direct_refund: 'approved_direct',
}

const FAILED_STAGES: RefundRequestProgressStage[] = ['team_handling', 'manual_by_team', 'not_done']

export function refundProgressSteps(
  progress: RefundRequestDetail['refund_progress'],
): ProgressStep[] {
  if (!progress) return []

  const approvedAt = progress.approved_at
  const steps: ProgressStep[] = [
    { key: APPROVED_STEP[progress.approved_by ?? ''] ?? 'approved_seller', state: 'done', at: approvedAt },
    { key: 'sent', state: 'done', at: approvedAt },
  ]

  if (!FAILED_STAGES.includes(progress.stage)) {
    steps.push(
      progress.stage === 'confirmed'
        ? { key: 'confirmed', state: 'done', at: progress.concluded_at }
        : { key: 'processing', state: 'current', at: null },
    )
    return steps
  }

  steps.push({ key: 'failed', state: 'warn', at: null })

  if (progress.stage === 'not_done') {
    steps.push({ key: 'not_done', state: 'warn', at: progress.concluded_at })
  } else if (progress.stage === 'manual_by_team') {
    steps.push({ key: 'manual_done', state: 'done', at: progress.concluded_at })
  } else {
    steps.push({ key: 'manual', state: 'current', at: null })
  }

  return steps
}

export function refundSentHintKey(paymentMethod: string | null | undefined): 'sent_pix' | 'sent_card' | 'sent_other' {
  if (paymentMethod === 'pix') return 'sent_pix'
  if (paymentMethod === 'credit_card') return 'sent_card'
  return 'sent_other'
}
